import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { connect } from 'react-redux';
import { compose, branch, renderNothing } from 'recompose';
import { Button, Glyphicon, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReactTable from 'react-table';
import { queryGetAllPerDiemsGQL } from '../graphql/getAllPerDiemsApi';
import { renderTicketNumber, getTicketState } from '../../tickets/utils';

import { toCentralTimeDisplayDate } from '../../../utils';

import {
  getBeforePageSize, getDepartmentId,
  getDistrictId,
  getFirstPageSize,
  getNextCursor,
  getPageSize, getPaymentDate,
  getPerDiemState,
  getPrevCursor, getRequestedAtDate, getRequestedForDate,
  getSorted, getTableDataRequest,
  getTicketJobNumber,
  getUserId,
} from '../selectors';

import {
  retryPayingPerDiem,
  setNextCursor,
  setPageSize,
  setPerDiemId,
  setPrevCursor,
  setTableOrder,
} from '../actions';

/* eslint-disable react/no-unused-prop-types */
const propTypes = exact({
  allPerDiems: PropTypes.array.isRequired,
  districtId: PropTypes.number,
  departmentId: PropTypes.number,
  perDiemState: PropTypes.string,
  userId: PropTypes.number,
  ticketJobNumber: PropTypes.string,
  requestedAt: PropTypes.string,
  requestedForDate: PropTypes.string,
  paymentDate: PropTypes.string,
  tableDataRequest: PropTypes.bool.isRequired,

  retryPayingPerDiemAction: PropTypes.func.isRequired,
  setPerDiemIdAction: PropTypes.func.isRequired,
  loading: PropTypes.bool,

  pageInfo: PropTypes.object,
  setPageSizeAction: PropTypes.func.isRequired,
  setNextCursorAction: PropTypes.func.isRequired,
  setPrevCursorAction: PropTypes.func.isRequired,
  currentPageSize: PropTypes.number.isRequired,

  nextCursor: PropTypes.string,
  prevCursor: PropTypes.string,
  last: PropTypes.number,
  first: PropTypes.number,

  rowCount: PropTypes.number,
  currentPage: PropTypes.number,
  pages: PropTypes.number,
  sorted: PropTypes.array.isRequired,
  setTableOrderAction: PropTypes.func.isRequired,
});
/* eslint-enable react/no-unused-prop-types */


const tooltip = (errormessage) => {
  return (
    <Tooltip id="tooltip">
      <div>
        <strong>
          {errormessage}
        </strong>
      </div>
    </Tooltip>
  );
};

const MaybeOverlayTrigger = (triggerProps) => {
  const { errormessage } = triggerProps;
  return (
    <OverlayTrigger placement="top" overlay={tooltip(errormessage)}>
      {triggerProps.children}
    </OverlayTrigger>
  );
};

const AllPerDiemsTable = (props) => {
  const {
    allPerDiems,
    retryPayingPerDiemAction,
    setPerDiemIdAction,
    loading,
  } = props;

  const {
    setPageSizeAction,
    currentPageSize,
    setNextCursorAction,
    setPrevCursorAction,
    setTableOrderAction,
    pages,
    currentPage,
    pageInfo,
    sorted,
  } = props;

  const handleNext = () => {
    if (!loading) {
      const { hasNextPage, endCursor } = pageInfo;
      if (hasNextPage) {
        setNextCursorAction(endCursor);
      }
    }
  };

  const handlePrevious = () => {
    if (!loading) {
      const { hasPreviousPage, startCursor } = pageInfo;
      if (hasPreviousPage) {
        setPrevCursorAction(startCursor);
      }
    }
  };

  const noData = () => {
    if (loading) {
      return null;
    }
    return <div className="rt-noData">No rows found</div>;
  };

  return (
    <React.Fragment>
      <ReactTable
        data={allPerDiems}
        loading={loading}
        manual
        NoDataComponent={noData}
        showPageJump={false}
        sorted={sorted}
        onSortedChange={(newSorted, column, shiftkey) => {
          const sortables = [];
          newSorted.forEach((sort, index) => {
            sortables.push({
              id: sort.id,
              order: index,
              desc: sort.desc,
            });
          });
          /* FIXME: When adding more than date sorting, update logic here */
          /* NOTE: desc field is a bool */
          setTableOrderAction(sortables);
        }}
        onPageSizeChange={(pageSize) => {
          setPageSizeAction(pageSize);
        }}
        PreviousComponent={() => {
          return (
            <React.Fragment>
              <Button onClick={handlePrevious}>Prev</Button>
            </React.Fragment>
          );
        }}
        NextComponent={() => {
          return (
            <React.Fragment>
              <Button onClick={handleNext}>Next</Button>
            </React.Fragment>
          );
        }}
        columns={[
          {
            Header: 'Requested At',
            id: 'requested_at',
            accessor: (e) => { return e.created_at; },
            Cell: (row) => {
              const createdAt = toCentralTimeDisplayDate(row.original.created_at.iso8601);
              return (
                <div>{createdAt}</div>
              );
            },
          },
          {
            Header: 'Requested For Date',
            id: 'requested_for',
            accessor: (e) => { return e.per_diem_date; },
            Cell: (row) => {
              const requestedDate = toCentralTimeDisplayDate(row.original.per_diem_date.iso8601);
              return (
                <div>{requestedDate}</div>
              );
            },
          },
          {
            Header: 'Requested By',
            id: 'fullname',
            filterable: false,
            sortable: false,
            accessor: (e) => { return e.requested_by.fullname; },
          },
          {
            Header: 'For Employee',
            id: 'for_employee',
            accessor: (e) => { return e.user.fullname; },
          },
          {
            Header: 'Ticket Number',
            id: 'ticket_number',
            accessor: (e) => { return e.per_diem_request.ticket_number.number; },
            Cell: (row) => {
              const ticketNumber = row.original.per_diem_request.ticket_number;
              return (
                <div style={{ padding: '7px 5px', color: '#000' }} className={`summary-details-${getTicketState(ticketNumber)}`}>
                  {renderTicketNumber(ticketNumber)}
                </div>
              );
            },
          },
          {
            Header: 'District',
            id: 'district',
            accessor: (e) => {
              if (e.per_diem_request.ticket_number.ticket) {
                return e.per_diem_request.ticket_number.ticket.district.name;
              }
              return '-';
            },
          },
          {
            Header: 'Department',
            id: 'department',
            accessor: (e) => {
              if (e.per_diem_request.ticket_number.ticket) {
                return e.per_diem_request.ticket_number.ticket.department.name;
              }
              return '-';
            },
          },
          {
            Header: 'State',
            id: 'state',
            accessor: (e) => { return e.state; },
          },
          {
            Header: 'Notes',
            id: 'notes',
            filterable: false,
            sortable: false,
            accessor: (e) => { return e.notes; },
          },
          {
            Header: 'Payment Date',
            id: 'payment_date',
            accessor: (e) => { return e.status; },
            Cell: (row) => {
              const { status } = row.original;
              if (!status) {
                return <div>-</div>;
              }

              const { payment_date: paymentDate } = row.original.status;
              if (!paymentDate) {
                return <div>-</div>;
              }
              const tzPaymentDate = toCentralTimeDisplayDate(paymentDate.iso8601);
              return <div>{tzPaymentDate}</div>;
            },
          },
          {
            Header: 'Payment Mechanism',
            id: 'status',
            accessor: (e) => { return e.status; },
            filterable: false,
            sortable: false,
            Cell: (row) => {
              const { status } = row.original;
              if (!status) {
                return <div>-</div>;
              }

              const { payment_mechanism: paymentMechanism } = row.original.status;

              if (paymentMechanism === 'rapid_pay') {
                return <div>Rapid Pay</div>;
              }
              if (paymentMechanism === 'settlement') {
                return <div>Settlement</div>;
              }

              return (
                <div>{paymentMechanism}</div>
              );
            },
          },
          {
            Header: 'Payment Status',
            id: 'status',
            accessor: (e) => { return e.status; },
            filterable: false,
            sortable: false,
            Cell: (row) => {
              const { status } = row.original;
              if (!status) {
                return <div>-</div>;
              }
              const paymentStatus = (status.payment_successful) ? 'Success'
                : (
                  <MaybeOverlayTrigger errormessage={status.payment_error}>
                    <Glyphicon glyph="comment" />
                  </MaybeOverlayTrigger>
                );
              return (
                <div>{paymentStatus}</div>
              );
            },
          },
          {
            Header: 'Debt',
            id: 'debt',
            accessor: (e) => { return e.debt_collected; },
            filterable: false,
            sortable: false,
            Cell: (row) => {
              const { status } = row.original;

              if (status === null || row.original.status.debt_collected === null) {
                return <div>-</div>;
              }

              const { debt_collected: debtCollected } = status;
              if (debtCollected) {
                return <div>Collected</div>;
              }

              return (
                <div>Uncollected</div>
              );
            },
          },
          {
            Header: 'Actions',
            id: 'actions',
            filterable: false,
            sortable: false,
            Cell: (row) => {
              const { status } = row.original;
              const retryPayment = (!status) ? false : status.retryable;

              return (
                retryPayment
                  && (
                  <Button
                    style={{ float: 'none' }}
                    bsStyle="danger"
                    bsSize="small"
                    onClick={() => {
                      setPerDiemIdAction(row.original.id);
                      retryPayingPerDiemAction();
                    }}
                  >
                    Retry
                  </Button>
                  )
              );
            },
          },
        ]}
        defaultPageSize={currentPageSize}
        pages={pages}
        page={currentPage}
        className="-striped -highlight"
      />
    </React.Fragment>
  );
};

AllPerDiemsTable.propTypes = propTypes;

const mapStateToProps = (state) => {
  return {
    districtId: getDistrictId(state),
    departmentId: getDepartmentId(state),
    perDiemState: getPerDiemState(state),
    userId: getUserId(state),
    ticketJobNumber: getTicketJobNumber(state),
    requestedAt: getRequestedAtDate(state),
    requestedForDate: getRequestedForDate(state),
    paymentDate: getPaymentDate(state),
    tableDataRequest: getTableDataRequest(state),

    currentPageSize: getPageSize(state),
    nextCursor: getNextCursor(state),
    prevCursor: getPrevCursor(state),
    first: getFirstPageSize(state),
    last: getBeforePageSize(state),
    sorted: getSorted(state),
  };
};

const mapDispatchToProps = {
  retryPayingPerDiemAction: retryPayingPerDiem,
  setPerDiemIdAction: setPerDiemId,
  setPageSizeAction: setPageSize,
  setNextCursorAction: setNextCursor,
  setPrevCursorAction: setPrevCursor,
  setTableOrderAction: setTableOrder,
};

const withHide = branch(
  (props) => { return props.tableDataRequest === false; },
  renderNothing,
);

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const enhance = compose(
  withRedux,
  withHide,
  queryGetAllPerDiemsGQL,
);


export default enhance(AllPerDiemsTable);
